import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { Building2, Plus, Edit, Trash2 } from 'lucide-react';
import { formatarCep, buscarEnderecoPorCep } from '../utils/cep';

const Condominios = () => {
  const { user } = useAuth();
  const { condominios, loadCondominios } = useCondominio();
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCondominio, setEditingCondominio] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    condominio_nome: '',
    condominio_cnpj: '',
    condominio_logradouro: '',
    condominio_numero: '',
    condominio_complemento: '',
    condominio_bairro: '',
    condominio_cidade: '',
    condominio_estado: '',
    condominio_cep: '',
    condominio_telefone: '',
    condominio_email: '',
    condominio_administradora_nome: '',
    condominio_administradora_cnpj: '',
    condominio_administradora_telefone: '',
    condominio_administradora_email: '',
    condominio_permite_visitantes: true,
    condominio_permite_encomendas: true,
    condominio_horario_inicio: '06:00',
    condominio_horario_fim: '22:00',
    condominio_dias_funcionamento: ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'],
    sindico_id: '',
    subsindico_id: ''
  });
  const [cepStatus, setCepStatus] = useState('');
  const [usuariosCondominio, setUsuariosCondominio] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await loadCondominios();
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);
    
    try {
      const payload = { ...formData };
      if (payload.sindico_id === '') payload.sindico_id = null;
      if (payload.subsindico_id === '') payload.subsindico_id = null;
      if (editingCondominio) {
        await api.put(`/condominios/${editingCondominio.condominio_id}`, payload);
      } else {
        await api.post('/condominios', payload);
      }
      setShowModal(false);
      setEditingCondominio(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Erro ao salvar condomínio:', error);
      
      // Tratar diferentes tipos de erro
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else if (error.response?.data?.errors) {
        // Se houver múltiplos erros de validação
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        setErrorMessage(errorMessages);
      } else if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Erro interno do servidor. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (condominio) => {
    setErrorMessage('');
    setEditingCondominio(condominio);
    setFormData({
      condominio_nome: condominio.condominio_nome || '',
      condominio_cnpj: condominio.condominio_cnpj || '',
      condominio_logradouro: condominio.condominio_logradouro || '',
      condominio_numero: condominio.condominio_numero || '',
      condominio_complemento: condominio.condominio_complemento || '',
      condominio_bairro: condominio.condominio_bairro || '',
      condominio_cidade: condominio.condominio_cidade || '',
      condominio_estado: condominio.condominio_estado || '',
      condominio_cep: condominio.condominio_cep || '',
      condominio_telefone: condominio.condominio_telefone || '',
      condominio_email: condominio.condominio_email || '',
      condominio_administradora_nome: condominio.condominio_administradora_nome || '',
      condominio_administradora_cnpj: condominio.condominio_administradora_cnpj || '',
      condominio_administradora_telefone: condominio.condominio_administradora_telefone || '',
      condominio_administradora_email: condominio.condominio_administradora_email || '',
      condominio_permite_visitantes: condominio.condominio_permite_visitantes || true,
      condominio_permite_encomendas: condominio.condominio_permite_encomendas || true,
      condominio_horario_inicio: condominio.condominio_horario_inicio || '06:00',
      condominio_horario_fim: condominio.condominio_horario_fim || '22:00',
      condominio_dias_funcionamento: condominio.condominio_dias_funcionamento || ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'],
      sindico_id: condominio.sindico_id || '',
      subsindico_id: condominio.subsindico_id || ''
    });
    setShowModal(true);
    if (condominio.condominio_id) {
      try {
        const res = await api.get(`/usuarios?condominio_id=${condominio.condominio_id}&limit=200`);
        const list = res.data.data?.usuarios || res.data.data || [];
        setUsuariosCondominio(Array.isArray(list) ? list : []);
      } catch {
        setUsuariosCondominio([]);
      }
    } else {
      setUsuariosCondominio([]);
    }
  };

  const handleDelete = async (condominio) => {
    const confirmado = window.confirm(
      `Tem certeza que deseja excluir o condomínio "${condominio.condominio_nome}"?\n\nEsta ação não pode ser desfeita.`
    );
    if (!confirmado) return;

    try {
      await api.delete(`/condominios/${condominio.condominio_id}`);
      setErrorMessage('');
      if (window.showNotification) {
        window.showNotification('Condomínio excluído com sucesso!', 'success');
      }
      loadData();
    } catch (error) {
      console.error('Erro ao excluir condomínio:', error);
      const message = error.response?.data?.message
        || error.message
        || 'Não foi possível excluir o condomínio.';
      setErrorMessage(message);
      if (window.showNotification) {
        window.showNotification(message, 'error', 8000);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      condominio_nome: '',
      condominio_cnpj: '',
      condominio_logradouro: '',
      condominio_numero: '',
      condominio_complemento: '',
      condominio_bairro: '',
      condominio_cidade: '',
      condominio_estado: '',
      condominio_cep: '',
      condominio_telefone: '',
      condominio_email: '',
      condominio_administradora_nome: '',
      condominio_administradora_cnpj: '',
      condominio_administradora_telefone: '',
      condominio_administradora_email: '',
      condominio_permite_visitantes: true,
      condominio_permite_encomendas: true,
      condominio_horario_inicio: '06:00',
      condominio_horario_fim: '22:00',
      condominio_dias_funcionamento: ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'],
      sindico_id: '',
      subsindico_id: ''
    });
    setErrorMessage('');
  };

  // Função para aplicar máscara de CPF ou CNPJ dinamicamente
  const applyCpfCnpjMask = (value) => {
    const numbers = value.replace(/\D/g, '');
    
    // CPF: 11 dígitos - 000.000.000-00
    if (numbers.length <= 11) {
      if (numbers.length <= 3) {
        return numbers;
      } else if (numbers.length <= 6) {
        return numbers.replace(/^(\d{3})(\d+)/, '$1.$2');
      } else if (numbers.length <= 9) {
        return numbers.replace(/^(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
      } else {
        return numbers.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      }
    }
    // CNPJ: 14 dígitos - 00.000.000/0000-00
    else if (numbers.length <= 14) {
      if (numbers.length <= 2) {
        return numbers;
      } else if (numbers.length <= 5) {
        return numbers.replace(/^(\d{2})(\d+)/, '$1.$2');
      } else if (numbers.length <= 8) {
        return numbers.replace(/^(\d{2})(\d{3})(\d+)/, '$1.$2.$3');
      } else if (numbers.length <= 12) {
        return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d+)/, '$1.$2.$3/$4');
      } else {
        return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      }
    }
    
    return value;
  };

  // Função para aplicar máscara de telefone
  const applyPhoneMask = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      if (numbers.length <= 10) {
        return numbers.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
      } else {
        return numbers.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
      }
    }
    return value;
  };

  const applyCEPMask = (value) => formatarCep(value);

  const preencherEnderecoPorCep = async (cepValue) => {
    const digits = String(cepValue || '').replace(/\D/g, '');
    if (digits.length !== 8) {
      setCepStatus('');
      return;
    }

    try {
      setCepStatus('Buscando endereço...');
      const endereco = await buscarEnderecoPorCep(digits);
      setFormData((prev) => ({
        ...prev,
        condominio_logradouro: endereco.logradouro || prev.condominio_logradouro,
        condominio_bairro: endereco.bairro || prev.condominio_bairro,
        condominio_cidade: endereco.cidade || prev.condominio_cidade,
        condominio_estado: endereco.estado || prev.condominio_estado,
        condominio_complemento: prev.condominio_complemento || endereco.complemento || ''
      }));
      setCepStatus('Endereço preenchido pelo CEP');
    } catch (error) {
      setCepStatus(error.message || 'Não foi possível consultar o CEP');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;

    // Aplicar máscaras específicas
    if (name === 'condominio_cnpj') {
      processedValue = applyCpfCnpjMask(value);
    } else if (name === 'condominio_administradora_cnpj') {
      processedValue = applyCpfCnpjMask(value);
    } else if (name === 'condominio_telefone' || name === 'condominio_administradora_telefone') {
      processedValue = applyPhoneMask(value);
    } else if (name === 'condominio_cep') {
      processedValue = applyCEPMask(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));

    if (name === 'condominio_cep') {
      preencherEnderecoPorCep(processedValue);
    }
  };

  // Função para formatar CPF/CNPJ para exibição
  const formatCpfCnpjForDisplay = (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    
    // CPF: 11 dígitos
    if (numbers.length === 11) {
      return numbers.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    }
    // CNPJ: 14 dígitos
    else if (numbers.length === 14) {
      return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    
    return value;
  };

  // Função para formatar telefone para exibição
  const formatPhoneForDisplay = (phone) => {
    if (!phone) return '';
    const numbers = phone.replace(/\D/g, '');
    if (numbers.length === 11) {
      return numbers.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    } else if (numbers.length === 10) {
      return numbers.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    }
    return phone;
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Condomínios</h1>
        {user?.tipo === 'administrador' && (
          <button
            onClick={() => {
              setEditingCondominio(null);
              resetForm();
              setShowModal(true);
            }}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Condomínio
          </button>
        )}
      </div>

      {errorMessage && !showModal && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-red-800">Não foi possível excluir o condomínio</h3>
          <p className="mt-1 text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* Lista de Condomínios */}
      <div className="card">
        <div className="card-body">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CPF/CNPJ</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Endereço</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {condominios.map((condominio) => (
                  <tr key={condominio.condominio_id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {condominio.condominio_nome}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatCpfCnpjForDisplay(condominio.condominio_cnpj)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div>
                        {condominio.condominio_logradouro}, {condominio.condominio_numero}
                      </div>
                      <div>
                        {condominio.condominio_bairro} - {condominio.condominio_cidade}/{condominio.condominio_estado}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatPhoneForDisplay(condominio.condominio_telefone)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`badge ${
                        condominio.condominio_ativo 
                          ? 'badge-success' 
                          : 'badge-danger'
                      }`}>
                        {condominio.condominio_ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(condominio)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {user?.tipo === 'administrador' && (
                          <button
                            onClick={() => handleDelete(condominio)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingCondominio ? 'Editar Condomínio' : 'Novo Condomínio'}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingCondominio(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mt-4">
                {/* Exibir mensagem de erro */}
                {errorMessage && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          Erro ao salvar condomínio
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                          <p>{errorMessage}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Dados Básicos */}
                    <div className="col-span-2">
                      <h4 className="text-md font-semibold text-gray-700 mb-2">Dados Básicos</h4>
                    </div>
                    
                    <div>
                      <label className="label">Nome *</label>
                      <input
                        type="text"
                        name="condominio_nome"
                        value={formData.condominio_nome}
                        onChange={handleInputChange}
                        required
                        maxLength="100"
                        className="input"
                      />
                    </div>
                    
                    <div>
                      <label className="label">CPF ou CNPJ *</label>
                      <input
                        type="text"
                        name="condominio_cnpj"
                        value={formData.condominio_cnpj}
                        onChange={handleInputChange}
                        required
                        maxLength="18"
                        placeholder="000.000.000-00 ou 00.000.000/0000-00"
                        className="input"
                      />
                    </div>

                    {/* Síndico / Subsíndico - apenas na edição */}
                    {editingCondominio && (
                      <>
                        <div className="col-span-2">
                          <h4 className="text-md font-semibold text-gray-700 mb-2 mt-4">Síndico / Subsíndico</h4>
                        </div>
                        <div>
                          <label className="label">Síndico</label>
                          <select
                            name="sindico_id"
                            value={formData.sindico_id || ''}
                            onChange={handleInputChange}
                            className="input"
                          >
                            <option value="">Nenhum</option>
                            {usuariosCondominio.map((u) => (
                              <option key={u.usuario_id} value={u.usuario_id}>
                                {u.usuario_nome || u.nome || u.email} {u.tipo_acesso ? `(${u.tipo_acesso})` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="label">Subsíndico</label>
                          <select
                            name="subsindico_id"
                            value={formData.subsindico_id || ''}
                            onChange={handleInputChange}
                            className="input"
                          >
                            <option value="">Nenhum</option>
                            {usuariosCondominio.map((u) => (
                              <option key={u.usuario_id} value={u.usuario_id}>
                                {u.usuario_nome || u.nome || u.email} {u.tipo_acesso ? `(${u.tipo_acesso})` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}

                    {/* Endereço */}
                    <div className="col-span-2">
                      <h4 className="text-md font-semibold text-gray-700 mb-2 mt-4">Endereço</h4>
                    </div>
                    
                    <div>
                      <label className="label">CEP *</label>
                      <input
                        type="text"
                        name="condominio_cep"
                        value={formData.condominio_cep}
                        onChange={handleInputChange}
                        required
                        maxLength="9"
                        placeholder="00000-000"
                        className="input"
                      />
                      {cepStatus && (
                        <p className="mt-1 text-xs text-gray-500">{cepStatus}</p>
                      )}
                    </div>

                    <div>
                      <label className="label">Número *</label>
                      <input
                        type="text"
                        name="condominio_numero"
                        value={formData.condominio_numero}
                        onChange={handleInputChange}
                        required
                        maxLength="20"
                        className="input"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <label className="label">Logradouro *</label>
                      <input
                        type="text"
                        name="condominio_logradouro"
                        value={formData.condominio_logradouro}
                        onChange={handleInputChange}
                        required
                        maxLength="255"
                        className="input"
                      />
                    </div>
                    
                    <div>
                      <label className="label">Complemento</label>
                      <input
                        type="text"
                        name="condominio_complemento"
                        value={formData.condominio_complemento}
                        onChange={handleInputChange}
                        maxLength="100"
                        className="input"
                      />
                    </div>
                    
                    <div>
                      <label className="label">Bairro *</label>
                      <input
                        type="text"
                        name="condominio_bairro"
                        value={formData.condominio_bairro}
                        onChange={handleInputChange}
                        required
                        maxLength="100"
                        className="input"
                      />
                    </div>
                    
                    <div>
                      <label className="label">Cidade *</label>
                      <input
                        type="text"
                        name="condominio_cidade"
                        value={formData.condominio_cidade}
                        onChange={handleInputChange}
                        required
                        maxLength="100"
                        className="input"
                      />
                    </div>
                    
                    <div>
                      <label className="label">Estado *</label>
                      <input
                        type="text"
                        name="condominio_estado"
                        value={formData.condominio_estado}
                        onChange={handleInputChange}
                        required
                        maxLength="2"
                        placeholder="SP"
                        className="input"
                      />
                    </div>

                    {/* Contato */}
                    <div className="col-span-2">
                      <h4 className="text-md font-semibold text-gray-700 mb-2 mt-4">Contato</h4>
                    </div>
                    
                    <div>
                      <label className="label">Telefone *</label>
                      <input
                        type="text"
                        name="condominio_telefone"
                        value={formData.condominio_telefone}
                        onChange={handleInputChange}
                        required
                        maxLength="15"
                        placeholder="(11) 99999-9999"
                        className="input"
                      />
                    </div>
                    
                    <div>
                      <label className="label">Email *  </label>
                      <input
                        type="email"
                        name="condominio_email"
                        value={formData.condominio_email}
                        onChange={handleInputChange}
                        maxLength="255"
                        className="input"
                      />
                    </div>

                    {/* Administradora */}
                    <div className="col-span-2">
                      <h4 className="text-md font-semibold text-gray-700 mb-2 mt-4">Administradora</h4>
                    </div>
                    
                    <div>
                      <label className="label">Nome da Administradora</label>
                      <input
                        type="text"
                        name="condominio_administradora_nome"
                        value={formData.condominio_administradora_nome}
                        onChange={handleInputChange}
                        maxLength="255"
                        className="input"
                      />
                    </div>
                    
                    <div>
                      <label className="label">CPF ou CNPJ da Administradora</label>
                      <input
                        type="text"
                        name="condominio_administradora_cnpj"
                        value={formData.condominio_administradora_cnpj}
                        onChange={handleInputChange}
                        maxLength="18"
                        placeholder="000.000.000-00 ou 00.000.000/0000-00"
                        className="input"
                      />
                    </div>
                    
                    <div>
                      <label className="label">Telefone da Administradora</label>
                      <input
                        type="text"
                        name="condominio_administradora_telefone"
                        value={formData.condominio_administradora_telefone}
                        onChange={handleInputChange}
                        maxLength="15"
                        placeholder="(11) 99999-9999"
                        className="input"
                      />
                    </div>
                    
                    <div>
                      <label className="label">Email da Administradora</label>
                      <input
                        type="email"
                        name="condominio_administradora_email"
                        value={formData.condominio_administradora_email}
                        onChange={handleInputChange}
                        maxLength="255"
                        className="input"
                      />
                    </div>

                    {/* Configurações */}
                    <div className="col-span-2">
                      <h4 className="text-md font-semibold text-gray-700 mb-2 mt-4">Configurações</h4>
                    </div>
                    
                    <div>
                      <label className="label">Horário de Início</label>
                      <input
                        type="time"
                        name="condominio_horario_inicio"
                        value={formData.condominio_horario_inicio}
                        onChange={handleInputChange}
                        className="input"
                      />
                    </div>
                    
                    <div>
                      <label className="label">Horário de Fim</label>
                      <input
                        type="time"
                        name="condominio_horario_fim"
                        value={formData.condominio_horario_fim}
                        onChange={handleInputChange}
                        className="input"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <div className="flex space-x-4">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="condominio_permite_visitantes"
                            checked={formData.condominio_permite_visitantes}
                            onChange={handleInputChange}
                            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">Permite Visitantes</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="condominio_permite_encomendas"
                            checked={formData.condominio_permite_encomendas}
                            onChange={handleInputChange}
                            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">Permite Encomendas</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setEditingCondominio(null);
                        resetForm();
                      }}
                      className="btn btn-secondary"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn btn-primary"
                    >
                      {isSubmitting ? 'Salvando...' : (editingCondominio ? 'Atualizar' : 'Criar')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Condominios;